//@flow
import * as React from 'react';
import classnames from 'classnames';
import { themr } from 'react-css-themr';
import {
    RadioGroup,
    RadioButton
} from 'common/components/core/form/RadioButton';
import { HandleOutsideClickKeyEventsDiv } from 'common/components/core/wrappers/Div';
import Button, { ButtonTypes } from 'common/components/core/button';
import { Icon } from 'common/components/core/icon';
import SingleDayPicker from 'common/components/core/date_picker/SingleDayPicker';
import moment from 'moment-timezone';

import theme from './DatePickerButton.scss';
import DateTimeUtils, { DateStyle } from 'common/utils/DateTimeUtils';

import coreIds from 'common/tests/core/coreIds.json';

type ShiftOptionType = {
    label: string,
    value: string
};
type Props = {
    selectedDate: Date,
    theme: Object,
    selectedRadioOptionId: string,
    radioList: Array<ShiftOptionType>,
    className?: string | any,
    radioListTitle: string,
    className: string,
    onDateChange: (date: Date) => void,
    onRadioGroupChange: (value: string) => void,
    hideDatePicker?: boolean,
    hideRangeSelector?: boolean,
    testId?: string
};
type State = {
    isVisible: boolean
};
const DefaultRadioList = (props: {
    theme: Object,
    radioListTitle: string,
    selectedRadioOptionId: string,
    list: Array<Object>,
    onRadioGroupChange: (value: string) => void
}) => {
    const {
        theme,
        onRadioGroupChange,
        selectedRadioOptionId,
        list,
        radioListTitle
    } = props;
    return list ? (
        <div className={classnames(theme.radioListWrap)}>
            <div className={theme.header}>{radioListTitle}</div>
            <RadioGroup
                className={theme.radioList}
                onChange={onRadioGroupChange}
                value={selectedRadioOptionId}
                theme={theme}
            >
                {list.map(listItem => {
                    return (
                        <RadioButton
                            key={listItem.value}
                            theme={theme}
                            label={listItem.label}
                            value={listItem.value}
                        />
                    );
                })}
            </RadioGroup>
        </div>
    ) : null;
};
const DefaultButton = ({
    theme,
    onClick,
    selectedDate,
    isVisible,
    selectedOption
}) => {
    return (
        <Button
            theme={theme}
            className={theme.datePickerButton}
            buttonType={ButtonTypes.Solid}
            semibold
            ripple={false}
            onClick={onClick}
        >
            <div className={theme.labelWrap}>
                <span className={theme.left}>
                    <span className={theme.option}>
                        {selectedOption && selectedOption.label}
                    </span>
                    <span className={theme.date}>
                        {DateTimeUtils.format(selectedDate, {
                            dateStyle: DateStyle.MEDIUM
                        })}
                    </span>
                </span>
                <Icon
                    theme={theme}
                    className={theme.arrowIconWrap}
                    icon={isVisible ? 'caret_up' : 'caret_down'}
                />
            </div>
        </Button>
    );
};

const datePickerButtonFactory = ({
    ButtonComponent = DefaultButton,
    RadioList = DefaultRadioList,
    DatePickerComponent = SingleDayPicker
}) => {
    class DatePickerButton extends React.PureComponent<Props, State> {
        state: State;
        constructor(props: Props) {
            super(props);
            this.state = {
                isVisible: false
            };
        }
        closeDateDropDown = () => {
            this.setState({ isVisible: false });
        };

        openDateDropDown = () => {
            this.setState({ isVisible: true });
        };

        toggleDateDropDown = () => {
            if (this.state.isVisible) {
                this.closeDateDropDown();
                return;
            }
            this.openDateDropDown();
        };

        handleDateChange = (date: Date) => {
            const { radioList, onDateChange } = this.props;
            if (!radioList || radioList.length <= 1) {
                this.closeDateDropDown();
            }
            onDateChange(date);
        };
        handleClickOutside = () => {
            this.closeDateDropDown();
        };
        getContainerRef = ref => {
            this.containerRef = ref;
        };
        getContainer = () => {
            return this.containerRef;
        };

        getSelectedOption(selectedRadioOptionId) {
            const { radioList } = this.props;
            return (
                radioList &&
                radioList.find(option => option.value === selectedRadioOptionId)
            );
        }
        render() {
            const {
                selectedDate,
                theme,
                selectedRadioOptionId,
                onRadioGroupChange,
                radioList,
                radioListTitle,
                className,
                datePickerClassName,
                children,
                hideDatePicker,
                hideRangeSelector,
                testId = coreIds.datePickerButton,
                ...others
            } = this.props;
            const { isVisible } = this.state;
            const selectedOption = this.getSelectedOption(
                selectedRadioOptionId
            );
            return (
                <div className={classnames(theme.datePickerItem, className)}>
                    <div
                        className={theme.buttonWrap}
                        ref={this.getContainerRef}
                    >
                        <ButtonComponent
                            {...others}
                            theme={theme}
                            className={theme.datePickerButton}
                            onClick={this.toggleDateDropDown}
                            selectedDate={selectedDate}
                            selectedOption={selectedOption}
                            isVisible={isVisible}
                            testId={testId}
                        />
                    </div>
                    {isVisible ? <div className={theme.overlay} /> : null}
                    {isVisible ? (
                        <HandleOutsideClickKeyEventsDiv
                            onEscape={this.closeDateDropDown}
                            getExtraContainer={this.getContainer}
                            handleClickOutside={this.handleClickOutside}
                            className={classnames(
                                theme.datePickerWrap,
                                datePickerClassName
                            )}
                        >
                            {children}
                            {!hideDatePicker && (
                                <DatePickerComponent
                                    {...others}
                                    theme={theme}
                                    date={moment(selectedDate)}
                                    onChange={this.handleDateChange}
                                    className={theme.DatePicker}
                                    autoOk
                                    sundayFirstDayOfWeek
                                    openDateDropDown={this.openDateDropDown}
                                    closeDateDropDown={this.closeDateDropDown}
                                />
                            )}
                            {!hideRangeSelector && (
                                <RadioList
                                    theme={theme}
                                    selectedRadioOptionId={
                                        selectedRadioOptionId
                                    }
                                    onRadioGroupChange={onRadioGroupChange}
                                    list={radioList}
                                    radioListTitle={radioListTitle}
                                />
                            )}
                        </HandleOutsideClickKeyEventsDiv>
                    ) : null}
                </div>
            );
        }
    }
    return themr('DatePickerButton', theme, {
        withRef: true
    })(DatePickerButton);
};

const ThemedDatePickerButton = datePickerButtonFactory({});
export { ThemedDatePickerButton as DatePickerButton, datePickerButtonFactory };
export default ThemedDatePickerButton;
